import React, { useState, useRef, useEffect } from 'react';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { api } from '../api/client';

const ICON = { business: '🏢', personal: '🏠' };

const FONT = "'Raleway', sans-serif";

function ChevronDown() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"
      style={{ display: 'block', flexShrink: 0 }}>
      <path d="M2 4.5L6 8.5L10 4.5" stroke="#888" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function WorkspaceSwitcher({ mobile = false }) {
  const { workspaces, activeWorkspaceId, switchWorkspace, loadWorkspaces, currentUser } = useCurrentUser();
  const [open, setOpen]       = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [nudging, setNudging] = useState(false);
  const ref = useRef(null);

  const hasPersonal  = workspaces.some(w => w.type === 'personal');
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const showSwitcher = workspaces.length > 1;
  const showCreate   = isSuperAdmin && !hasPersonal;

  // One-time discoverability nudge (Step 3)
  useEffect(() => {
    if (workspaces.length < 2 || currentUser?.preferences?.switcherNudgeSeen) return;
    setNudging(true);
    const t = setTimeout(() => {
      setNudging(false);
      api('/api/users/me', {
        method: 'PUT',
        body: JSON.stringify({ preferences: { switcherNudgeSeen: true } }),
      }).catch(() => {});
    }, 2200);
    return () => clearTimeout(t);
  }, [workspaces.length, currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click/tap
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  if (!showSwitcher && !showCreate) return null;

  const active = workspaces.find(w => w.id === activeWorkspaceId);

  async function handleCreate() {
    if (creating) return;
    setCreating(true);
    setCreateError('');
    try {
      const res  = await api('/api/workspaces/personal', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) { setCreateError(data.error || 'Failed to create personal workspace.'); return; }
      await loadWorkspaces();
      switchWorkspace(data.id);
      setOpen(false);
    } catch {
      setCreateError('Failed to create personal workspace. Please try again.');
    } finally {
      setCreating(false);
    }
  }

  // Trigger button style — matches Sort-by select visual language
  const triggerStyle = {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    width: '100%', boxSizing: 'border-box', textAlign: 'left',
    padding: mobile ? '0 0.9rem' : '0.45rem 0.75rem',
    minHeight: mobile ? 44 : 'auto',
    background: '#f8f8f8', border: '1px solid #e0e0e0',
    borderRadius: mobile ? 10 : 8,
    cursor: 'pointer', fontFamily: FONT,
    fontSize: mobile ? '0.9rem' : '0.82rem', color: '#444',
  };

  const dropdownStyle = {
    position: 'absolute', top: '100%', left: 0, right: 0,
    background: '#fff', border: '1px solid #e0e4ea', borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 300,
    overflow: 'hidden', marginTop: 4,
  };

  const itemStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    width: '100%', boxSizing: 'border-box', minHeight: 44,
    padding: '0.55rem 0.9rem', border: 'none', textAlign: 'left',
    fontSize: '0.85rem', fontFamily: FONT, cursor: 'pointer',
    background: isActive ? '#f0f4ff' : '#fff',
    fontWeight: isActive ? 600 : 400, color: '#333',
  });

  return (
    <div ref={ref} style={{ position: 'relative', marginBottom: mobile ? 0 : '0.5rem' }}>

      {showSwitcher ? (
        <button
          onClick={() => setOpen(o => !o)}
          title="Switch workspace"
          style={triggerStyle}
          className={`ws-trigger${nudging ? ' ws-nudge' : ''}`}
        >
          <span style={{ fontSize: '0.9rem' }}>{ICON[active?.type] || '🏢'}</span>
          <span style={{ flex: 1 }}>{active?.name || 'Workspace'}</span>
          <ChevronDown />
        </button>
      ) : (
        <button
          onClick={handleCreate}
          disabled={creating}
          title="Add personal workspace"
          style={{ ...triggerStyle, color: '#667eea' }}
          className="ws-trigger"
        >
          <span style={{ fontSize: '0.9rem' }}>➕</span>
          <span style={{ flex: 1 }}>{creating ? 'Creating...' : 'Add personal workspace'}</span>
        </button>
      )}

      {open && (
        <div style={dropdownStyle}>
          {workspaces.map(w => {
            const isActive = w.id === activeWorkspaceId;
            return (
              <button
                key={w.id}
                onClick={() => { switchWorkspace(w.id); setOpen(false); }}
                style={itemStyle(isActive)}
              >
                <span>{ICON[w.type] || '🏢'}</span>
                <span style={{ flex: 1 }}>{w.name}</span>
                {isActive && <span style={{ color: '#667eea', fontSize: '0.8rem' }}>✓</span>}
              </button>
            );
          })}
          {showCreate && (
            <>
              <button
                onClick={handleCreate}
                disabled={creating}
                style={{ ...itemStyle(false), borderTop: '1px solid #eee', color: '#667eea' }}
              >
                <span>➕</span>
                <span style={{ flex: 1 }}>{creating ? 'Creating...' : 'Add personal workspace'}</span>
              </button>
              {createError && (
                <p style={{ margin: 0, padding: '0.3rem 0.9rem', fontSize: '0.78rem', color: '#dc2626' }}>
                  {createError}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {createError && !open && (
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.78rem', color: '#dc2626' }}>{createError}</p>
      )}
    </div>
  );
}
